<template>
  <div class="w-full flex flex-row">
    <div class="flex flex-col h-full w-full">
      <contacts-header
        ref="contactsHeader"
        :search-query="searchQuery"
        :header-title="pageTitle"
        :segments-id="segmentsId"
        :is-locked-segment="isDailyTaskSegment"
        this-selected-contact-id=""
        @on-input-search="onInputSearch"
        @on-create-filter="onCreateFilter"
        @on-toggle-create="onToggleCreate"
        @on-toggle-filter="onToggleFilters"
        @on-search-submit="onSearchSubmit"
        @on-toggle-import="onToggleImport"
        @on-export-submit="onExportSubmit"
        @on-toggle-report="onToggleReport"
        @on-toggle-save-filter="onToggleSaveFilters"
        @on-toggle-delete-filter="onToggleDeleteFilters"
        @on-toggle-edit-filter="onToggleFilters"
        @on-state-search="onStateSearch"
        @on-status-search="onStatusSearch"
        @on-column-config-change="onColumnConfigChange"
      />
      <!-- Bulk Actions Panel -->
      <contact-bulk-actions
        v-if="selectedContacts.length > 0"
        :contacts="selectedContactsData"
        :all-contacts-selected="allContactsOnPageSelected"
        @select-all-contacts="onSelectAllContacts"
        @assign-agent="onAssignAgentBulk"
        @assign-labels="onAssignLabelsBulk"
        @update-status="onUpdateStatusBulk"
      />
      <contacts-table
        ref="contactsTable"
        :contacts="records"
        :show-search-empty-state="showEmptySearchResult"
        :is-loading="uiFlags.isFetching"
        :on-click-contact="openContactInfoPanel"
        :active-contact-id="selectedContactId"
        :sort-param="currentSortParam"
        :sort-order="currentSortOrder"
        :visible-columns="columnConfig.visibleColumns"
        :column-order="columnConfig.columnOrder"
        :selected-contacts="selectedContacts"
        @on-sort-change="onSortChange"
        @contact-deleted="onContactDeleted"
        @select-contact="onSelectContact"
        @deselect-contact="onDeselectContact"
        @select-all-contacts="onSelectAllContactsFromTable"
        @deselect-all-contacts="onDeselectAllContacts"
      />
      <table-footer
        :current-page="Number(meta.currentPage)"
        :total-count="meta.count"
        :page-size="20"
        @page-change="onPageChange"
      />
    </div>

    <add-custom-views
      v-if="showAddSegmentsModal"
      :custom-views-query="segmentsQuery"
      :filter-type="filterType"
      :open-last-saved-item="openSavedItemInSegment"
      @close="onCloseAddSegmentsModal"
    />
    <delete-custom-views
      v-if="showDeleteSegmentsModal"
      :show-delete-popup.sync="showDeleteSegmentsModal"
      :active-custom-view="activeSegment"
      :custom-views-id="segmentsId"
      :active-filter-type="filterType"
      :open-last-item-after-delete="openLastItemAfterDeleteInSegment"
      @close="onCloseDeleteSegmentsModal"
    />
    <div
      v-if="showContactViewPane"
      class="modal-overlay-v1"
      @click.stop="closeContactInfoPanel"
    ></div>
    <contact-info-panel
      v-if="showContactViewPane"
      :contact="Contact"
      :on-close="closeContactInfoPanel"
      :is-modal-view="true"
      @contact-deleted="onContactDeleted"
    />
    <create-filter
      :show="showCreateFilter"
      @cancel="onCreateFilter"
      v-on-clickaway="hideFilter"
    />
    <!-- <create-contact :show="showCreateModal" @cancel="onToggleCreate" /> -->
    <div>
      <new-create-contact :show="showCreateModal" @cancel="onToggleCreate" />
    </div>

    <woot-modal :show.sync="showImportModal" :on-close="onToggleImport">
      <import-contacts v-if="showImportModal" :on-close="onToggleImport" />
    </woot-modal>
    <send-report v-if="showReportModal" :show="showReportModal" @close="onToggleReport" />
    <div class="contacts-advanced-filters-modal-host">
      <woot-modal
        :show.sync="showFiltersModal"
        :on-close="closeAdvanceFiltersModal"
        size="medium"
      >
        <contacts-advanced-filters
          v-if="showFiltersModal"
          ref="contactAdvancedFilters"
          :on-close="closeAdvanceFiltersModal"
          :initial-filter-types="contactFilterItems"
          :initial-action-types="contactActionsItems"
          :initial-applied-filters="appliedFilter"
          :initial-applied-actions="appliedActions"
          :leads="lastactivity"
          :active-segment-name="activeSegmentName"
          :is-segments-view="hasActiveSegments"
          :states-for-filter="contactStates"
          :is-daily-tasks-view="isDailyTasksView"
          @applyFilter="onApplyFilter"
          @updateSegment="onUpdateSegment"
          @clearFilters="clearFilters"
        />
      </woot-modal>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import CreateFilter from '../../conversation/contact/CreateFilter.vue';
import NewCreateContact from '../../conversation/contact/NewCreateContact.vue';
import ContactsHeader from './Header';
import ContactsTable from './ContactsTableVueTables2'; // Using vue-tables-2 instead of ve-table
import ContactInfoPanel from './ContactInfoPanel';
import CreateContact from 'dashboard/routes/dashboard/conversation/contact/CreateContact';
import TableFooter from 'dashboard/components/widgets/TableFooter';
import ImportContacts from './ImportContacts.vue';
import SendReport from './SendReport.vue';
import ContactsAdvancedFilters from './ContactsAdvancedFilters.vue';
import ContactBulkActions from 'dashboard/components/widgets/contactBulkActions/Index.vue';
import contactFilterItems from '../contactFilterItems';
import contactActionsItems from "../contactActions"
import filterQueryGenerator from '../../../../helper/filterQueryGenerator';
import { preserveSavedFilterGroupId } from '../../../../helper/filterGroupHelper';
import AddCustomViews from 'dashboard/routes/dashboard/customviews/AddCustomViews';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews';
import { CONTACTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
import alertMixin from 'shared/mixins/alertMixin';
import countries from 'shared/constants/countries.js';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import {
  dailyTaskUserIdFromView,
  findDailyTaskView,
  isDailyTaskView,
} from 'dashboard/helper/dailyTaskCustomViews';
import { mixin as clickaway } from 'vue-clickaway';
import types from '../../../../store/mutation-types';
import axios from 'axios';
import { debounce } from '@chatwoot/utils';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import {
  buildUpdatedAccountPreferences,
  contactSegmentExists,
  isSameAccountPreference,
  labelExists,
} from 'dashboard/helper/userPreferencesHelper';

const DEFAULT_PAGE = 1;
const FILTER_TYPE_CONTACT = 1;
const CONTACTS_LIST_ROUTE_NAMES = [
  'contacts_dashboard',
  'contacts_segments_dashboard',
  'contacts_labels_dashboard',
];
const ALL_DAILY_TASK_USERS_ID = 'all';

export default {
  components: {
    ContactsHeader,
    ContactsTable,
    TableFooter,
    ContactInfoPanel,
    CreateContact,
    ImportContacts,
    SendReport,
    ContactsAdvancedFilters,
    ContactBulkActions,
    AddCustomViews,
    DeleteCustomViews,
    CreateFilter,
    NewCreateContact,
  },
  mixins: [alertMixin, clickaway, uiSettingsMixin],
  props: {
    label: { type: String, default: '' },
    segmentsId: {
      type: [String, Number],
      default: 0,
    },
    dailyTasksView: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchQuery: '',
      showCreateModal: false,
      showImportModal: false,
      showReportModal: false,
      showCreateFilter: false,
      selectedContactId: '',
      // Default sort: created_at desc, but the underlying scope on the API
      // side coalesces with reengaged_at, so duplicate Facebook leads bubble
      // to the top while the UI sort indicator stays on "Created At".
      sortConfig: { created_at: 'desc' },
      showFiltersModal: false,
      contactActionsItems: contactActionsItems.map(filter =>({
        ...filter,
        attributeName: this.$t(
          `CONTACTS_FILTER.ATTRIBUTES.${filter.attributeI18nKey}`
        ),
      })),
      segmentsQuery: {},
      filterType: FILTER_TYPE_CONTACT,
      showAddSegmentsModal: false,
      showDeleteSegmentsModal: false,
      appliedFilter: [],
      appliedActions:[],
      segmentActionApplyInFlight: false,
      status_id: null,
      state_id: null,
      columnConfig: {
        visibleColumns: [],
        columnOrder: [],
      },
      // Bulk action selection state
      selectedContacts: [],
      /** Account states (CRM) for advanced filter / segment edit UI */
      contactStates: [],
      dailyTaskExplicitSort: false,
    };
  },
  beforeDestroy() {
    bus.$off('state-search', this.onStateSearch);
    bus.$off('status-search', this.onStatusSearch);
    bus.$off('on-apply-filter-submit', this.onApplyFilter);
    bus.$off('on-assign-agent',this.submitFilterQueries);
    // bus.$off('input-search', this.onInputSearch);
  },
  computed: {
    ...mapGetters({
      records: 'contacts/getContacts',
      uiFlags: 'contacts/getUIFlags',
      meta: 'contacts/getMeta',
      segments: 'customViews/getCustomViews',
      getAppliedContactFilters: 'contacts/getAppliedContactFilters',
      agentList: 'agents/getAgents',
      labels: 'labels/getLabels',
      campaigns: 'campaigns/getAllCampaigns',
      boards: 'boards/getBoards',
      getContact: 'contacts/getContact',  // ← ADD THIS
      currentUser: 'getCurrentUser',
    }),
    showEmptySearchResult() {
      const hasEmptyResults = !!this.searchQuery && this.records.length === 0;
      return hasEmptyResults;
    },
    currentSortParam() {
      return Object.keys(this.sortConfig)[0] || 'created_at';
    },
    currentSortOrder() {
      return Object.values(this.sortConfig)[0] || 'desc';
    },
    hasAppliedFilters() {
      return this.getAppliedContactFilters.length;
    },
    hasActiveSegments() {
      return this.activeSegment && this.segmentsId !== 0;
    },
    isDailyTaskSegment() {
      return isDailyTaskView(this.activeSegment);
    },
    isDailyTasksView() {
      return (
        this.dailyTasksView ||
        this.$route.name === 'contacts_daily_tasks' ||
        this.isDailyTaskSegment
      );
    },
    isContactAndLabelDashboard() {
      return (
        this.$route.name === 'contacts_dashboard' ||
        this.$route.name === 'contacts_labels_dashboard' ||
        this.isDailyTasksView
      );
    },
    isViewingAllDailyTasks() {
      if (this.isDailyTaskSegment) {
        return dailyTaskUserIdFromView(this.activeSegment) === 'all';
      }
      const { user_id: userId, all_users: allUsers } = this.$route.query;
      return allUsers === 'true' || userId === ALL_DAILY_TASK_USERS_ID;
    },
    contactFilterItems() {
      return contactFilterItems.map(filter => ({
        ...filter,
        attributeName: this.$t(
          `CONTACTS_FILTER.ATTRIBUTES.${filter.attributeI18nKey}`
        ),
      }));
    },
    dailyTaskOwnerName() {
      if (this.isViewingAllDailyTasks) return '';
      if (this.isDailyTaskSegment) {
        const userId = dailyTaskUserIdFromView(this.activeSegment);
        if (userId && String(userId) === String(this.currentUser?.id)) {
          return this.currentUser?.name || '';
        }
        const agent = this.agentList.find(
          user => String(user.id || user.user_id) === String(userId)
        );
        return agent?.name || agent?.user?.name || this.activeSegment?.name || '';
      }
      const userId = this.$route.query.user_id;
      if (userId) {
        const agent = this.agentList.find(
          user => String(user.id || user.user_id) === String(userId)
        );
        return agent?.name || agent?.user?.name || '';
      }
      return this.currentUser?.name || '';
    },
    pageTitle() {
      if (this.isDailyTasksView) {
        if (this.isViewingAllDailyTasks) {
          return this.$t('CONTACTS_PAGE.DAILY_TASKS_ALL_HEADER');
        }
        if (this.dailyTaskOwnerName) {
          return this.$t('CONTACTS_PAGE.DAILY_TASKS_USER_HEADER', {
            name: this.dailyTaskOwnerName,
          });
        }
        return this.$t('CONTACTS_PAGE.DAILY_TASKS_HEADER');
      }
      if (this.hasActiveSegments) {
        return this.activeSegment.name;
      }
      if (this.label) {
        return `#${this.label}`;
      }
      return this.$t('CONTACTS_PAGE.HEADER');
    },
    dailyTaskRequestParams() {
      if (!this.isDailyTasksView) {
        return {};
      }
      const params = { daily_tasks: true };
      if (this.isDailyTaskSegment) {
        const userId = dailyTaskUserIdFromView(this.activeSegment);
        if (userId === 'all') {
          params.all_users = true;
        } else if (userId) {
          params.user_id = userId;
        }
        return params;
      }
      const { user_id: userId, all_users: allUsers } = this.$route.query;
      if (allUsers === 'true' || userId === 'all') {
        params.all_users = true;
      } else if (userId) {
        params.user_id = userId;
      }
      return params;
    },
    lastactivity() {
      return [
        { id: 1, name: 'User' },
        { id: 2, name: 'Agent' },
      ];
    },

    Contact() {
      if (this.selectedContactId) {
        const contact = this.getContact(this.selectedContactId);
        return contact && contact.id ? contact : null;
      }
      return null;
    },
    showContactViewPane() {
      // Only show modal when we have BOTH the ID and the loaded contact data
      return this.selectedContactId !== '' && this.Contact && this.Contact.id;
    },
    wrapClass() {
      return this.showContactViewPane ? 'w-[75%]' : 'w-full';
    },
    pageParameter() {
      // console.log("Full route:", this.$route.fullPath);
      //  console.log("Query parameters:", this.$route.query);
      //  console.log("Page parameter (raw):", this.$route.query.page);
      const selectedPageNumber = Number(this.$route.query?.page);
      // console.log("selectedPageNumber",selectedPageNumber)
      return !Number.isNaN(selectedPageNumber) &&
        selectedPageNumber >= DEFAULT_PAGE
        ? selectedPageNumber
        : DEFAULT_PAGE;
    },
    activeSegment() {
      if (
        this.segmentsId === null ||
        this.segmentsId === undefined ||
        this.segmentsId === '' ||
        String(this.segmentsId) === '0'
      ) {
        return undefined;
      }
      const sid = Number(this.segmentsId);
      const [match] = this.segments.filter(
        view => Number(view.id) === sid
      );
      return match;
    },
    activeSegmentName() {
      return this.activeSegment?.name;
    },
    contactFilters() {
      return this.$store.getters['contacts/getContactFilters'];
    },
    // Bulk action computed properties
    selectedContactsData() {
      return this.records.filter(contact => this.selectedContacts.includes(contact.id));
    },
    allContactsOnPageSelected() {
      return this.records.length > 0 && this.selectedContacts.length === this.records.length;
    },
  },
  watch: {
    // label() {
    //   this.fetchContacts(DEFAULT_PAGE);
    //   if (this.hasAppliedFilters) {
    //     this.clearFilters();
    //   }
    // },

    //  meta: {
    //     handler(newMeta) {
    //      console.log('Meta updated:', newMeta);
    //    },
    //  deep: true
    // },

    $route: {
      async handler(to, from) {
        // Clear selections when page changes
        this.selectedContacts = [];

        if (from && to.path !== from.path) {
          this.searchQuery = '';
        }

        if (
          to.name === 'contacts_labels_dashboard' ||
          to.name === 'contacts_segments_dashboard'
        ) {
          const isValid = await this.ensureValidContactViewRoute(to);
          if (!isValid) return;
        }

        const isSegmentRoute =
          to.name === 'contacts_segments_dashboard' &&
          to.params.id != null &&
          String(to.params.id) !== '0' &&
          String(to.params.id) !== '';

        const enteredOrSwitchedSegment =
          isSegmentRoute &&
          (from.name !== 'contacts_segments_dashboard' ||
            String(to.params.id) !== String(from.params.id));
        const enteredDailyTasks =
          to.name === 'contacts_daily_tasks' &&
          from.name !== 'contacts_daily_tasks';
        const switchedDailyTaskUser =
          to.name === 'contacts_daily_tasks' &&
          from.name === 'contacts_daily_tasks' &&
          (String(to.query.user_id) !== String(from.query.user_id) ||
            String(to.query.all_users) !== String(from.query.all_users));

        if (enteredOrSwitchedSegment || enteredDailyTasks || switchedDailyTaskUser) {
          this.beginSegmentContactsLoad();
        }

        if (to.name === 'contacts_segments_dashboard' && this.isDailyTaskSegment) {
          this.dailyTaskExplicitSort = false;
          this.sortConfig = { daily_task: 'asc' };
        }

        const url = new URL(window.location.href);
        const pageParam = url.searchParams.get('page');
        const selectedPageNumber = Number(pageParam) || DEFAULT_PAGE;

        if (to.name === 'contacts_daily_tasks') {
          this.fetchContacts(selectedPageNumber);
          return;
        }

        const sameContactView =
          from &&
          to.name === from.name &&
          String(to.params.id || '') === String(from.params.id || '');
        if (this.segmentActionApplyInFlight && !sameContactView) {
          this.segmentActionApplyInFlight = false;
        }

        if (
          !(this.segmentActionApplyInFlight && sameContactView) &&
          selectedPageNumber >= 1 &&
          !this.hasAppliedFilters
        ) {
          if (to.name === 'contacts_segments_dashboard') {
            if (this.isDailyTaskSegment) {
              this.fetchContacts(selectedPageNumber);
            } else if (this.hasActiveSegments) {
              this.fetchSavedFilteredContact(
                this.activeSegment.query,
                selectedPageNumber,
                this.searchQuery
              );
            }
          } else {
            this.fetchContacts(selectedPageNumber);
          }
        }

        const routeToSave = this.getRouteToSaveLastContactView(to, from);
        if (routeToSave) {
          this.debouncedSaveLastContactView(routeToSave);
        }
      },
      // Don't use immediate: true — initial load is handled by mounted (onSortChange → submitFilterQueries → fetchContacts).
      // This avoids double fetch on load; watcher still runs when route actually changes (e.g. back/forward, link).
    },

    // Also watch records to debug data updates
    records: {
      handler(newRecords) {
        const url = new URL(window.location.href);
        const currentUrlPage = url.searchParams.get('page') || DEFAULT_PAGE;

        // console.log('Records updated:', {
        //   count: newRecords.length,
        //   urlPage: currentUrlPage,
        //   metaPage: this.meta.currentPage,
        //   currentUrl: window.location.href
        // });
      },
      deep: true,
    },

    activeSegment(newVal, oldVal) {
      // Skip initial run when there is no segment to avoid duplicate fetch (mounted already triggers via submitFilterQueries)
      if (newVal === undefined && oldVal === undefined) return;
      // Saving a segment replaces its record and would refetch before the action job finishes.
      if (this.segmentActionApplyInFlight) {
        const sameSegment =
          newVal && oldVal && Number(newVal.id) === Number(oldVal.id);
        if (sameSegment) return;
        this.segmentActionApplyInFlight = false;
      }
      if (this.isDailyTaskSegment || this.isDailyTasksView) {
        this.fetchContacts(DEFAULT_PAGE);
      } else if (this.hasActiveSegments) {
        const payload = this.activeSegment.query;
        this.fetchSavedFilteredContact(payload, DEFAULT_PAGE, this.searchQuery);
      } else if (this.hasAppliedFilters && this.$route.name === 'contacts_dashboard') {
        this.fetchFilteredContacts(DEFAULT_PAGE);
      } else if (this.$route.name === 'contacts_segments_dashboard') {
        // Avoid loading full contacts list while on segment route (stale vuex otherwise).
        return;
      } else {
        this.fetchContacts(DEFAULT_PAGE);
      }
    },
  },
  created() {
    this.debouncedSaveLastContactView = debounce(route => {
      this.saveLastContactView(route);
    }, 500);
  },
  beforeMount() {
    if (this.isDailyTasksView) {
      this.beginSegmentContactsLoad();
      return;
    }
    if (
      this.$route.name === 'contacts_segments_dashboard' &&
      String(this.segmentsId) !== '0' &&
      this.segmentsId != null &&
      this.segmentsId !== ''
    ) {
      this.beginSegmentContactsLoad();
    }
  },
  async mounted() {
    if (this.$route.name === 'contacts_daily_tasks') {
      await this.$store.dispatch('customViews/get', 'contact');
    }

    const isValidRoute = await this.ensureValidContactViewRoute(this.$route);

    if (this.$route.name === 'contacts_daily_tasks') {
      await this.redirectDailyTasksRouteToCustomView();
    }
    if (this.isDailyTasksView) {
      this.dailyTaskExplicitSort = false;
      this.sortConfig = { daily_task: 'asc' };
      this.onSortChange({ daily_task: 'asc' });
    } else {
      this.onSortChange({ created_at: 'desc' });
    }
    // Initial load: submitFilterQueries (from onSortChange) fetches contacts or applies filter — no duplicate fetch here
    bus.$on('on-assign', this.onAssign);
    bus.$on('on-apply-filter-submit', this.onApplyFilter);
    bus.$on('on-assign-agent',this.submitFilterQueries);
    // Page URL is synced by fetchContacts / onApplyFilter / submitFilterQueries — avoid overwriting a page-1 reset here
    // this.clearFilters();
    if (this.contactFilters && this.contactFilters.length > 0) {
      this.onApplyFilter(this.contactFilters);
    }

    // Fetch required data for segment editing
    this.$store.dispatch('agents/get');
    this.$store.dispatch('labels/get');
    this.$store.dispatch('boards/get');
    this.$store.dispatch('attributes/get');
    // Fetch campaign filter options and set filter attributes
    await this.$store.dispatch('campaigns/fetchCampaignFilterOptions');
    await this.fetchContactStatesForFilters();

    if (isValidRoute && this.isContactsListRoute(this.$route)) {
      this.saveLastContactView(this.$route);
    }
  },
  methods: {
    async fetchContactStatesForFilters() {
      const accountId = this.$route.params.accountId;
      if (accountId == null || String(accountId).trim() === '') {
        this.contactStates = [];
        return;
      }
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${accountId}/states`,
          { params: { only_data: 'true' } }
        );
        const rows = data.states_data || [];
        this.contactStates = rows
          .filter(s => s && s.id != null)
          .map(s => ({ id: s.id, name: s.name }));
      } catch {
        this.contactStates = [];
      }
    },
    /**
     * Drop stale list from the previous route (e.g. main contacts) so the segment
     * view shows the loader first, then segment results — not a flash of all contacts.
     */
    beginSegmentContactsLoad() {
      this.$store.commit(`contacts/${types.CLEAR_CONTACTS}`);
      this.$store.commit(`contacts/${types.SET_CONTACT_META}`, {
        count: 0,
        current_page: 1,
      });
      this.$store.commit(`contacts/${types.SET_CONTACT_UI_FLAG}`, {
        isFetching: true,
      });
    },
    updatePageParam(page) {
      const params = new URLSearchParams();
      const query = { ...this.$route.query, page };
      Object.keys(query).forEach(key => {
        const value = query[key];
        if (value != null && value !== '') params.set(key, value);
      });
      const search = params.toString();
      window.history.pushState(
        {},
        null,
        `${this.$route.path}${search ? `?${search}` : ''}`
      );
    },
    getSortAttribute() {
      if (this.isDailyTasksView && !this.dailyTaskExplicitSort) {
        return 'daily_task';
      }
      let sortAttr = Object.keys(this.sortConfig).reduce((acc, sortKey) => {
        const sortOrder = this.sortConfig[sortKey];
        if (sortOrder) {
          const sortOrderSign = sortOrder === 'asc' ? '' : '-';
          return `${sortOrderSign}${sortKey}`;
        }
        return acc;
      }, '');
      if (!sortAttr) {
        if (this.isDailyTasksView) {
          return 'daily_task';
        }
        this.sortConfig = { last_activity_at: 'desc' };
        sortAttr = '-last_activity_at';
      }
      return sortAttr;
    },

    fetchContacts(page) {
      if (this.isContactAndLabelDashboard) {
        this.$store.commit('contacts/SET_CONTACT_META', { current_page: page });
        this.updatePageParam(page);
        let value = '';
        if (this.searchQuery.charAt(0) === '+') {
          value = this.searchQuery.substring(1);
        } else {
          value = this.searchQuery;
        }

        const requestParams = {
          page,
          sortAttr: this.getSortAttribute(),
          label: this.label,
          ...(this.status_id ? { status_id: this.status_id } : {}),
          ...(this.state_id ? { state_id: this.state_id } : {}),
          ...(this.getAppliedContactFilters?.length > 0 ? { filters: this.getAppliedContactFilters } : {}),
          ...this.dailyTaskRequestParams,
        };

        if (!value && !this.status_id && !this.state_id) {
          this.$store.dispatch('contacts/get', requestParams);
        } else if (this.status_id || this.state_id) {
          this.$store.dispatch('contacts/search', {
            search: encodeURIComponent(value),
            ...requestParams,
          });
        } else if (value) {
          this.$store.dispatch('contacts/search', {
            search: encodeURIComponent(value),
            ...requestParams,
          });
        }
      }
    },

    async redirectDailyTasksRouteToCustomView() {
      if (this.$route.name !== 'contacts_daily_tasks') return false;

      const { user_id: userId, all_users: allUsers } = this.$route.query;
      const views = this.segments;
      let match = null;
      if (allUsers === 'true' || userId === ALL_DAILY_TASK_USERS_ID) {
        match = findDailyTaskView(views, 'all');
      } else if (userId) {
        match = findDailyTaskView(views, userId);
      } else {
        match = findDailyTaskView(views, this.currentUser?.id);
      }
      if (!match?.id) return false;

      const query = {};
      if (this.$route.query.page) query.page = this.$route.query.page;
      await this.$router.replace({
        name: 'contacts_segments_dashboard',
        params: { ...this.$route.params, id: String(match.id) },
        query,
      });
      return true;
    },
    fetchSavedFilteredContact(payload, page, search = '', options = {}) {
      if (this.isDailyTaskSegment) {
        this.fetchContacts(page);
        return;
      }
      if (this.hasActiveSegments) {
        this.updatePageParam(page);
        let value = '';
        if (search && typeof search === 'string') {
          value = search.charAt(0) === '+' ? search.substring(1) : search;
        }
        this.$store.dispatch('contacts/filter', {
          queryPayload: payload,
          page,
          sortAttr: this.getSortAttribute(),
          search: value ? encodeURIComponent(value) : '',
          isRefetchAfterAction: Boolean(options.isRefetchAfterAction),
        });
      }
    },
    fetchFilteredContacts(page) {
      if (this.hasAppliedFilters) {
        this.updatePageParam(page);
        this.dispatchContactFilter(page);
      }
    },

    onInputSearch(event) {
      const newQuery = event.target.value;
      const refetchAllContacts = !!this.searchQuery && newQuery === '';
      this.searchQuery = newQuery;
      if (refetchAllContacts) {
        if (this.hasActiveSegments) {
          const payload = this.activeSegment?.query;
          this.fetchSavedFilteredContact(payload, DEFAULT_PAGE, newQuery);
        } else {
          this.fetchContacts(DEFAULT_PAGE);
        }
      }
    },
    onSearchSubmit() {
      this.selectedContactId = '';
      if (this.searchQuery) {
        if (this.hasActiveSegments) {
          const payload = this.activeSegment?.query;
          this.fetchSavedFilteredContact(payload, DEFAULT_PAGE, this.searchQuery);
        } else {
          this.fetchContacts(DEFAULT_PAGE);
        }
      }
    },
    onPageChange(page) {
      this.updatePageParam(page);
      this.$router.push({
        path: this.$route.path, // Keep the current path
        query: { ...this.$route.query, page: page }, // Keep all other queries, update page
      });
      this.selectedContactId = '';
      if (this.isDailyTasksView) {
        if (this.hasAppliedFilters) {
          this.dispatchContactFilter(page);
        } else {
          this.fetchContacts(page);
        }
      } else if (this.segmentsId !== 0) {
        const payload = this.activeSegment.query;
        this.fetchSavedFilteredContact(payload, page, this.searchQuery);
      } else if (this.hasAppliedFilters) {
        this.fetchFilteredContacts(page);
      } else {
        this.fetchContacts(page);
      }
    },
    openContactInfoPanel(contactId) {
      this.selectedContactId = contactId;
    },
    closeContactInfoPanel() {
      this.selectedContactId = '';
    },
    onToggleCreate() {
      this.showCreateModal = !this.showCreateModal;
    },
    onToggleSaveFilters() {
      this.showAddSegmentsModal = true;
    },
    onCloseAddSegmentsModal() {
      this.showAddSegmentsModal = false;
    },
    onToggleDeleteFilters() {
      this.showDeleteSegmentsModal = true;
    },
    onCloseDeleteSegmentsModal() {
      this.showDeleteSegmentsModal = false;
    },
    onToggleImport() {
      this.showImportModal = !this.showImportModal;
    },
    onToggleReport() {
      this.showReportModal = !this.showReportModal;
    },
    isContactsListRoute(route) {
      return Boolean(
        route?.name && CONTACTS_LIST_ROUTE_NAMES.includes(route.name)
      );
    },
    getRouteToSaveLastContactView(to, from) {
      if (this.isContactsListRoute(to)) return to;
      if (from && this.isContactsListRoute(from)) return from;
      return null;
    },
    async clearLastContactViewPreference() {
      const accountId = this.$route.params.accountId;
      if (!accountId) return;

      const { uiSettings } = buildUpdatedAccountPreferences(
        accountId,
        this.$store.getters.getCurrentUser,
        'last_contact_view',
        { type: 'all' }
      );
      await this.$store.dispatch('updateUISettings', { uiSettings });
    },
    async ensureValidContactViewRoute(route = this.$route) {
      const { accountId } = route.params;
      if (!accountId) return true;

      if (route.name === 'contacts_labels_dashboard' && route.params.label) {
        try {
          await this.$store.dispatch('labels/getFresh');
        } catch {
          return true;
        }
        if (!labelExists(this.labels, route.params.label)) {
          await this.clearLastContactViewPreference();
          if (this.$route.name === route.name) {
            await this.$router.replace({
              name: 'contacts_dashboard',
              params: { accountId },
            });
          }
          return false;
        }
      }

      if (
        route.name === 'contacts_segments_dashboard' &&
        route.params.id != null &&
        String(route.params.id) !== '0' &&
        route.params.id !== ''
      ) {
        if (!this.segments.length) {
          await this.$store.dispatch('customViews/get', 'contact');
        }
        if (!contactSegmentExists(this.segments, route.params.id)) {
          await this.clearLastContactViewPreference();
          if (this.$route.name === route.name) {
            await this.$router.replace({
              name: 'contacts_dashboard',
              params: { accountId },
            });
          }
          return false;
        }
      }

      return true;
    },
    async saveLastContactView(route = this.$route) {
      try {
        if (!this.isContactsListRoute(route)) return;

        const accountId = route.params.accountId;
        if (!accountId) return;

        let viewData = {
          type: 'all',
        };

        if (
          route.name === 'contacts_segments_dashboard' &&
          route.params.id != null &&
          String(route.params.id) !== '0' &&
          route.params.id !== ''
        ) {
          viewData.type = 'segment';
          viewData.id = String(route.params.id);
        } else if (
          route.name === 'contacts_labels_dashboard' &&
          route.params.label
        ) {
          viewData.type = 'label';
          viewData.id = route.params.label;
        }

        const currentUser = this.$store.getters.getCurrentUser;
        if (
          isSameAccountPreference(
            currentUser,
            accountId,
            'last_contact_view',
            viewData
          )
        ) {
          return;
        }

        const { uiSettings } = buildUpdatedAccountPreferences(
          accountId,
          currentUser,
          'last_contact_view',
          viewData
        );
        await this.$store.dispatch('updateUISettings', { uiSettings });
      } catch (error) {
        console.error('Failed to save last contact view:', error);
      }
    },
    onSortChange(params) {
      const sortKey = Object.keys(params).find(key => params[key]);
      if (this.isDailyTasksView) {
        this.dailyTaskExplicitSort = Boolean(sortKey && sortKey !== 'daily_task');
      }
      this.sortConfig = params;
      // this.fetchContacts(this.meta.currentPage);
      this.submitFilterQueries({ resetToFirstPage: true });

      const sortBy =
        Object.entries(params).find(pair => Boolean(pair[1])) || [];

      this.$track(CONTACTS_EVENTS.APPLY_SORT, {
        appliedOn: sortBy[0],
        order: sortBy[1],
      });
    },
    async onToggleFilters() {
      if (this.hasActiveSegments) {
        // Ensure agents, labels, campaigns, and boards are loaded before initializing segment
        await Promise.all([
          this.$store.dispatch('agents/get'),
          this.$store.dispatch('labels/get'),
          this.$store.dispatch('boards/get'),
          this.$store.dispatch('campaigns/fetchCampaignFilterOptions'),
        ]);
        this.initializeSegmentToFilterModal(this.activeSegment);
      }
      this.showFiltersModal = true;
    },
    closeAdvanceFiltersModal() {
      if (this.$refs.contactAdvancedFilters?.actionConfirmOpen) return;
      this.showFiltersModal = false;
      this.appliedFilter = [];
    },


    dispatchContactFilter(page, extra = {}) {
      return this.$store.dispatch('contacts/filter', {
        queryPayload: filterQueryGenerator(this.getAppliedContactFilters),
        page,
        sortAttr: this.getSortAttribute(),
        ...this.dailyTaskRequestParams,
        ...extra,
      });
    },
    submitFilterQueries(options = {}) {
      const resetToFirstPage = Boolean(options.resetToFirstPage);
      let newFilter = [...this.getAppliedContactFilters];

      if (newFilter.length === 0) {
        if (this.$route.name === 'contacts_segments_dashboard') {
          if (this.isDailyTaskSegment) {
            this.fetchContacts(this.pageParameter);
            return;
          }
          if (this.hasActiveSegments) {
            this.fetchSavedFilteredContact(
              this.activeSegment.query,
              this.pageParameter,
              this.searchQuery
            );
          }
          return;
        }
        this.fetchContacts(this.pageParameter);
        return;
      }
      const page = resetToFirstPage ? DEFAULT_PAGE : this.pageParameter;
      if (resetToFirstPage) {
        this.$router.replace({
          path: this.$route.path,
          query: { ...this.$route.query, page: DEFAULT_PAGE },
        });
        this.updatePageParam(DEFAULT_PAGE);
      }
      this.dispatchContactFilter(page);
      this.showFiltersModal = false;
    },
    syncContactsListPageInUrl(page) {
      this.$router.replace({
        path: this.$route.path,
        query: { ...this.$route.query, page },
      });
      this.updatePageParam(page);
    },
    onApplyFilter(payload) {
      this.closeContactInfoPanel();
      this.segmentsQuery = filterQueryGenerator(payload.filters);
      const page = DEFAULT_PAGE;
      const sortAttr = this.getSortAttribute();

      this.syncContactsListPageInUrl(page);

      this.$store
        .dispatch('contacts/filter', {
          queryPayload: filterQueryGenerator(payload.filters),
          actions: payload.actions,
          isActions: payload.isActions,
          page,
          sortAttr,
          ...this.dailyTaskRequestParams,
        })
        .then(() => {
          if (payload.isActions) {
            setTimeout(() => {
              this.$store.dispatch('contacts/filter', {
                queryPayload: this.segmentsQuery,
                page,
                sortAttr: this.getSortAttribute(),
                isRefetchAfterAction: true,
                ...this.dailyTaskRequestParams,
              });
            }, 2500);
          }
        });
      this.showFiltersModal = false;
    },
    onUpdateSegment(payload, segmentName, actionPayload = {}) {
      if (this.isDailyTaskSegment) return;
      const filters = Array.isArray(payload) ? payload : [];
      const actions = Array.isArray(actionPayload?.actions)
        ? actionPayload.actions
        : [];
      const isActions = Boolean(actionPayload?.isActions && actions.length);
      const query = filterQueryGenerator(filters);
      const payloadData = {
        ...this.activeSegment,
        name: segmentName,
        query,
      };

      if (isActions) {
        this.segmentActionApplyInFlight = true;
        this.applySegmentContactActions(query, actions);
      }

      this.$store.dispatch('customViews/update', payloadData);
      this.closeAdvanceFiltersModal();
    },
    applySegmentContactActions(query, actions) {
      this.closeContactInfoPanel();
      this.segmentsQuery = query;
      const page = DEFAULT_PAGE;
      const sortAttr = this.getSortAttribute();
      this.syncContactsListPageInUrl(page);

      this.$store
        .dispatch('contacts/filter', {
          queryPayload: query,
          actions,
          isActions: true,
          page,
          sortAttr,
          ...this.dailyTaskRequestParams,
        })
        .then(() => {
          setTimeout(() => {
            if (!this.segmentActionApplyInFlight) return;
            this.segmentActionApplyInFlight = false;
            if (this.$route.name !== 'contacts_segments_dashboard') return;
            this.fetchSavedFilteredContact(query, page, this.searchQuery, {
              isRefetchAfterAction: true,
            });
          }, 2500);
        });
    },
    clearFilters() {
      this.$store.dispatch('contacts/clearContactFilters');
      this.fetchContacts(this.pageParameter);
    },
    onExportSubmit() {
      try {
        const q = this.searchQuery || "" ;
        const filters = this.hasAppliedFilters ? this.segmentsQuery : null
        this.$store.dispatch('contacts/export', { q, filters });
        this.showAlert(this.$t('EXPORT_CONTACTS.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(
          error.message || this.$t('EXPORT_CONTACTS.ERROR_MESSAGE')
        );
      }
    },
    setParamsForEditSegmentModal() {
      // Here we are setting the params for edit segment modal to show the existing values.

      // For agent, labels, campaigns, and boards we get only the id's from the query.
      // So we are mapping the id's to the actual values.

      // For custom attributes we get only attribute key.
      // So we are mapping it to find the input type of the attribute to show in the edit segment modal.
      const params = {
        agents: this.agentList,
        states: this.contactStates,
        labels: this.labels.map(label => ({ id: label.id, name: label.title })),
        campaigns: this.campaigns,
        boards: this.boards,
        countries: countries,
        filterTypes: contactFilterItems,
        allCustomAttributes:
          this.$store.getters['attributes/getAttributesByModel'](
            'contact_attribute'
          ),
      };
      return params;
    },
    initializeSegmentToFilterModal(activeSegment) {
      const query = activeSegment?.query?.payload;
      if (!Array.isArray(query)) return;

      this.appliedFilter.push(
        ...query.map(filter => {
          const params = this.setParamsForEditSegmentModal();
          let values = [];
          
          // Handle date/datetime values - preserve as strings
          if (filter.attribute_key === 'created_at' || filter.attribute_key === 'last_activity_at') {
            // For date filters with 'between' operator, keep as array; otherwise extract first value
            if (filter.filter_operator === 'between') {
              values = filter.values;
            } else if (Array.isArray(filter.values)) {
              // For single date operators (is_greater_than, is_lesser_than, etc.), use the first value
              values = filter.values[0] || '';
            } else {
              values = filter.values;
            }
          } else if (Array.isArray(filter.values)) {
            values = generateValuesForEditCustomViews(filter, params);
          }

          return {
            attribute_key: filter.attribute_key,
            attribute_model: filter.attribute_model,
            filter_operator: filter.filter_operator,
            values: values,
            query_operator: filter.query_operator,
            custom_attribute_type: filter.custom_attribute_type,
            // Preserve statusValues from saved filter for board_id and campaign_id
            statusValues: filter.statusValues || null,
            ...preserveSavedFilterGroupId(filter),
          };
        })
      );
    },
    openSavedItemInSegment() {
      const lastItemInSegments = [...this.segments]
        .filter(view => !isDailyTaskView(view))
        .pop();
      if (!lastItemInSegments) {
        this.$router.push({
          name: 'contacts_dashboard',
          params: { accountId: this.$route.params.accountId },
        });
        this.fetchContacts(DEFAULT_PAGE);
        return;
      }
      this.$router.push({
        name: 'contacts_segments_dashboard',
        params: {
          accountId: this.$route.params.accountId,
          id: lastItemInSegments.id,
        },
      });
    },
    openLastItemAfterDeleteInSegment() {
      this.openSavedItemInSegment();
    },
    onCreateFilter() {
      this.showCreateFilter = !this.showCreateFilter;
    },
    hideFilter() {
      this.showCreateFilter = false;
    },
    onAssign() {
      const currentPage = this.pageParameter;
      if (this.isDailyTasksView) {
        this.fetchContacts(currentPage);
        return;
      }
      // Handle segments view
      if (this.segmentsId !== 0 && this.activeSegment) {
        const payload = this.activeSegment.query;
        this.fetchSavedFilteredContact(payload, currentPage, this.searchQuery);
      } else if (this.hasAppliedFilters) {
        // Handle applied filters - use getAppliedContactFilters from store
        this.$store.dispatch('contacts/filter', {
          queryPayload: filterQueryGenerator(this.getAppliedContactFilters),
          page: currentPage,
          sortAttr: this.getSortAttribute(),
        });
      } else {
        this.fetchContacts(currentPage);
      }
    },
    onStateSearch(id) {
      this.state_id = id;
      this.fetchContacts(this.pageParameter);
    },
    onStatusSearch(id) {
      this.status_id = id;
      this.fetchContacts(this.pageParameter);
    },
    onContactDeleted() {
      this.selectedContactId = '';
      const currentPage = this.meta.currentPage || 1;
      const pageSize = 15;

      let pageToFetch = currentPage;
      if (this.records.length === 0 && currentPage > 1) {
        pageToFetch = currentPage - 1;
        this.$router.push({
          path: this.$route.path,
          query: { ...this.$route.query, page: pageToFetch },
        });
      }

      // Handle segments view
      if (this.segmentsId !== 0 && this.activeSegment) {
        const payload = this.activeSegment.query;
        this.fetchSavedFilteredContact(payload, pageToFetch, this.searchQuery);
      } else if (this.hasAppliedFilters) {
        // Handle applied filters - use getAppliedContactFilters from store
        this.$store.dispatch('contacts/filter', {
          queryPayload: filterQueryGenerator(this.getAppliedContactFilters),
          page: pageToFetch,
          sortAttr: this.getSortAttribute(),
        });
      } else {
        // Fetch regular contacts
        this.fetchContacts(pageToFetch);
      }
    },
    onColumnConfigChange(config) {
      this.columnConfig = config;
    },
    // Bulk action methods - Selection handlers
    onSelectContact(contactId) {
      if (!this.selectedContacts.includes(contactId)) {
        this.selectedContacts.push(contactId);
      }
    },
    onDeselectContact(contactId) {
      this.selectedContacts = this.selectedContacts.filter(id => id !== contactId);
    },
    onSelectAllContactsFromTable(contactIds) {
      this.selectedContacts = [...contactIds];
    },
    onDeselectAllContacts() {
      this.selectedContacts = [];
    },
    onSelectAllContacts(checked) {
      if (checked) {
        this.selectedContacts = this.records.map(contact => contact.id);
      } else {
        this.selectedContacts = [];
      }
    },
    // Bulk action handlers
    async onAssignAgentBulk(agent) {
      try {
        await this.$store.dispatch('bulkActions/process', {
          type: 'Contact',
          ids: this.selectedContacts,
          fields: {
            assignee_id: agent.id,
          },
        });
        this.selectedContacts = [];
        if (agent.id) {
          this.showAlert(this.$t('BULK_ACTION.CONTACT_ASSIGN_SUCCESFUL'));
        } else {
          this.showAlert(this.$t('BULK_ACTION.CONTACT_UNASSIGN_SUCCESFUL'));
        }
        // Refresh contacts after bulk action
        this.refreshContactsAfterBulkAction();
      } catch (err) {
        this.showAlert(this.$t('BULK_ACTION.CONTACT_ASSIGN_FAILED'));
      }
    },
    async onAssignLabelsBulk(labels) {
      try {
        await this.$store.dispatch('bulkActions/process', {
          type: 'Contact',
          ids: this.selectedContacts,
          labels: {
            add: labels,
          },
        });
        this.selectedContacts = [];
        this.showAlert(this.$t('BULK_ACTION.LABELS.ASSIGN_SUCCESFUL'));
        // Refresh contacts after bulk action
        this.refreshContactsAfterBulkAction();
      } catch (err) {
        this.showAlert(this.$t('BULK_ACTION.LABELS.ASSIGN_FAILED'));
      }
    },
    async onUpdateStatusBulk(statusData) {
      try {
        await this.$store.dispatch('bulkActions/process', {
          type: 'Contact',
          ids: this.selectedContacts,
          fields: statusData,
        });
        this.selectedContacts = [];
        this.showAlert(this.$t('BULK_ACTION.CONTACT_STATUS.UPDATE_SUCCESFUL'));
        // Refresh contacts after bulk action
        this.refreshContactsAfterBulkAction();
      } catch (err) {
        this.showAlert(this.$t('BULK_ACTION.CONTACT_STATUS.UPDATE_FAILED'));
      }
    },
    refreshContactsAfterBulkAction() {
      // Short delay to allow backend to process
      setTimeout(() => {
        const currentPage = this.pageParameter;
        if (this.segmentsId !== 0 && this.activeSegment) {
          const payload = this.activeSegment.query;
          this.fetchSavedFilteredContact(payload, currentPage, this.searchQuery);
        } else if (this.hasAppliedFilters) {
          this.$store.dispatch('contacts/filter', {
            queryPayload: filterQueryGenerator(this.getAppliedContactFilters),
            page: currentPage,
            sortAttr: this.getSortAttribute(),
          });
        } else {
          this.fetchContacts(currentPage);
        }
      }, 500);
    },
  },
};
</script>
<style scoped>
.modal-overlay-v1 {
  position: fixed;
  left: 0;
  top: 0;
  z-index: 39;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.4);
  width: 100%;
  height: 100%;
}

/*
 * Contacts table uses z-index up to 9999 for in-cell edit UIs (see ContactsTableVueTables2).
 * Default woot-modal `.modal-mask` is z-index 34, so the segment / advanced-filters modal can
 * render underneath the table on some viewports. Lift only this modal host (no global CSS).
 */
.contacts-advanced-filters-modal-host ::v-deep .modal-mask {
  z-index: var(--z-modal-mask, 10100);
}

.contacts-advanced-filters-modal-host ::v-deep .action-confirm-host .modal-mask {
  z-index: 10250;
}
</style>
